import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { type NavItem, type BadgeState, isPathActive, getNavPath } from '@/lib/navigationItems';

interface NavItemButtonProps {
  item: NavItem;
  badgeState?: BadgeState;
  artistId?: string;
  onNavigate?: () => void;
  className?: string;
  /** Compact tiles like artist directory cards (settings + support). */
  variant?: 'default' | 'utility';
}

/**
 * Reusable menu item button component
 * Used in slide-down menus (UsersMenu, ArtistsMenu, etc.)
 */
export function NavItemButton({
  item,
  badgeState = {},
  artistId,
  onNavigate,
  className,
  variant = 'default',
}: NavItemButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const path = getNavPath(item, artistId);
  const isActive = isPathActive(location.pathname, item.pathMatches) || 
    (artistId && location.pathname === `/artist/${artistId}`);
  
  const badge = item.getBadge?.(badgeState);

  const handleClick = () => {
    onNavigate?.();
    if (path === '#') {
      return;
    }
    navigate(path);
  };

  return (
    <Button
      variant="ghost"
      className={cn(
        variant === 'utility'
          ? 'flex-1 min-w-0 h-12 justify-center rounded-md bg-card px-4 text-base font-medium text-foreground gap-2 border-0 shadow-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/25 relative [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground transition-colors'
          : 'w-full justify-center h-12 text-base font-medium relative',
        isActive &&
          (variant === 'utility' ? 'bg-muted/60 [&_svg]:text-foreground' : 'text-primary'),
        className,
      )}
      onClick={handleClick}
    >
      <span
        className={cn(
          'flex min-w-0 items-center',
          variant === 'utility' ? 'justify-center gap-2' : 'gap-2',
        )}
      >
        <div className="relative shrink-0">
          {item.icon({
            className: cn('shrink-0', variant === 'utility' ? 'size-4' : 'h-4 w-4'),
          })}
        </div>
        <span
          className={cn(
            'relative flex items-center gap-1',
            variant === 'utility' && 'min-w-0 flex-1 justify-center',
          )}
        >
          <span className={variant === 'utility' ? 'truncate' : undefined}>{t(item.labelKey)}</span>
          {/* Badge with count - positioned absolutely to the right of text */}
          {badge?.show && badge.count && (
            <div className="absolute left-full ml-1 flex items-center justify-center min-w-[0.75rem] h-3 bg-primary rounded-full text-[0.5rem] text-white font-medium px-0.5 -translate-y-1.5">
              {badge.count}
            </div>
          )}
          {/* Dot badge (no count) - positioned to the right of text */}
          {badge?.show && badge.dot && !badge.count && (
            <div className="absolute left-full ml-1 w-2 h-2 bg-primary rounded-full -translate-y-1.5" />
          )}
        </span>
      </span>
    </Button>
  );
}
